import React, { useState, useEffect, useRef } from 'react';
import imageCache from '@/utils/imageCache';
import './FacadeImage.styles.css';

interface FacadeImageProps {
  affaireId: string;
  className?: string;
}

const DEFAULT_IMAGE_URL = '/images/default.jpg';

export const FacadeImage: React.FC<FacadeImageProps> = ({
  affaireId,
  className = ''
}) => {
  // Check cache immediately on mount to avoid loading skeleton
  const cacheKey = `facade-${affaireId}`;
  const cachedUrl = imageCache.get(cacheKey);

  const [imageUrl, setImageUrl] = useState<string | null>(cachedUrl);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(!!cachedUrl); // If cached, skip lazy loading
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading (skip if already cached)
  useEffect(() => {
    // If we already have a cached image, skip lazy loading
    if (cachedUrl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [cachedUrl]);

  // Load image when visible
  useEffect(() => {
    if (!isVisible || !affaireId) return;

    // Check cache first
    const cacheKey = `facade-${affaireId}`;
    const cachedUrl = imageCache.get(cacheKey);

    if (cachedUrl) {
      setImageUrl(cachedUrl);
      setImageLoading(false);
      return;
    }

    let isMounted = true;
    let blobUrl: string | null = null;

    const loadFacadeImage = async () => {
      try {
        setImageLoading(true);
        setImageError(false);

        // Direct image URL - no metadata call needed
        // Backend handles: BMP conversion, thumbnail generation, caching
        const API_BASE_URL = 'https://myapp.chatelainxpert.be';
        const imageUrl = `${API_BASE_URL}/api/fm/image/facade/${affaireId}`;

        // Fetch the image directly (public endpoint, no auth needed for cached images)
        const imageResponse = await fetch(imageUrl);

        if (imageResponse.ok) {
          const blob = await imageResponse.blob();
          blobUrl = URL.createObjectURL(blob);

          if (isMounted) {
            imageCache.set(cacheKey, blobUrl);
            setImageUrl(blobUrl);
          }
        } else if (isMounted) {
          setImageError(true);
        }
      } catch (err) {
        console.error('Error loading facade image:', err);
        if (isMounted) {
          setImageError(true);
        }
      } finally {
        if (isMounted) {
          setImageLoading(false);
        }
      }
    };

    loadFacadeImage();

    // Cleanup function - don't revoke blob URL, let cache manage it
    return () => {
      isMounted = false;
      // Blob URL is cached and will be revoked when cache expires
    };
  }, [affaireId, isVisible]);

  if (!isVisible || imageLoading) {
    return (
      <div ref={containerRef} className={`facade-thumb-container loading ${className}`}>
        <div className="facade-thumb-skeleton">
          <div className="skeleton-pulse"></div>
        </div>
      </div>
    );
  }

  if (imageError || !imageUrl) {
    // Use static default image URL - browser will cache it automatically
    return (
      <div ref={containerRef} className={`facade-thumb-container ${className}`}>
        <img
          src={DEFAULT_IMAGE_URL}
          alt="Facade"
          className="facade-thumb-image"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`facade-thumb-container ${className}`}>
      <img
        src={imageUrl}
        alt="Facade"
        className="facade-thumb-image"
        loading="lazy"
        onError={(e) => {
          // If image fails to load, use default image
          const target = e.target as HTMLImageElement;
          if (target.src !== '/images/default.jpg') {
            target.src = '/images/default.jpg';
            setImageError(true);
          }
        }}
      />
    </div>
  );
};